import * as l from "@cerulea/lexicon";
import { Post, type Post as PostRecord } from "../structs.ts";
import { RpcClient } from "./api.ts";
import { type RecordRef, recordRpcs, storedMatch } from "./pds.ts";
import { canWriteCollection } from "./permissions.ts";

const posts = recordRpcs(l.literal(Post.id), Post);
const tidAlphabet = "234567abcdefghijklmnopqrstuvwxyz";
let clockId: bigint | undefined;
let lastMicros = 0n;

export function canCreatePosts(scopes: ReadonlySet<string>): boolean {
  return canWriteCollection(scopes, Post.id, "create");
}

export function rkeyOf(uri: string): string {
  return uri.slice(uri.lastIndexOf("/") + 1);
}

export function createTid(now = Date.now()): string {
  if (clockId === undefined) {
    clockId = BigInt(crypto.getRandomValues(new Uint16Array(1))[0]! & 0x3ff);
  }
  const wallClock = BigInt(now) * 1000n;
  const micros = wallClock > lastMicros ? wallClock : lastMicros + 1n;
  lastMicros = micros;
  let value = (micros << 10n) | clockId;
  let tid = "";
  for (let index = 0; index < 13; index++) {
    tid = tidAlphabet[Number(value & 31n)]! + tid;
    value >>= 5n;
  }
  return tid;
}

export async function publishPost(
  record: PostRecord,
  rkey: string,
  signal?: AbortSignal,
): Promise<RecordRef> {
  const { authState, pdsTransportFor } = await import("./auth.ts");
  const state = authState.get();
  if (state.status !== "authenticated") throw new Error("Sign in before publishing");
  if (!canCreatePosts(state.scopes)) throw new Error("This session cannot create Cerulea posts");
  const repo = state.did as `did:${string}`;
  const client = new RpcClient(pdsTransportFor(repo));
  const address = { repo, collection: Post.id, rkey };
  try {
    return await client.call(posts.create, {
      parameters: {},
      input: { ...address, record },
      ...(signal && { signal }),
    });
  } catch (error) {
    const stored = await storedMatch(client, posts, address, record, signal);
    if (!stored) throw error;
    return stored;
  }
}

async function ownPostClient(uri: string, action: "update" | "delete") {
  const { authState, pdsRequest } = await import("./auth.ts");
  const state = authState.get();
  const [repo, collection, rkey] = uri.slice("at://".length).split("/");
  if (state.status !== "authenticated" || state.did !== repo) {
    throw new Error(`Sign in as the post's author to ${action} it`);
  }
  if (!canWriteCollection(state.scopes, Post.id, action)) {
    throw new Error(`This session cannot ${action} Cerulea posts`);
  }
  if (collection !== Post.id || !rkey) throw new Error("Not a Cerulea post");
  return {
    client: new RpcClient(pdsRequest),
    address: { repo: repo as `did:${string}`, collection: Post.id, rkey },
  };
}

export async function deletePost(
  post: { uri: string; cid: string },
  signal?: AbortSignal,
): Promise<void> {
  const { client, address } = await ownPostClient(post.uri, "delete");
  await client.call(posts.delete, {
    parameters: {},
    input: { ...address, swapRecord: post.cid },
    ...(signal && { signal }),
  });
}

/** Replace a post in place; `swapRecord` refuses to clobber an edit made elsewhere. */
export async function updatePost(
  post: { uri: string; cid: string },
  record: PostRecord,
  signal?: AbortSignal,
): Promise<RecordRef> {
  const { client, address } = await ownPostClient(post.uri, "update");
  return await client.call(posts.put, {
    parameters: {},
    input: { ...address, record, swapRecord: post.cid },
    ...(signal && { signal }),
  });
}
